/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, Input, Signal} from '@angular/core';
import {NgForm} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {StateService, UIRouter} from '@uirouter/core';
import template from './creative-edit-page.component.html';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {CreativeEditBehavior, CreativeEditBehaviorService} from './creative-edit-setup';
import {NavigationHistoryService} from '../../components/util/navigation-history.service';
import {TargetingEditorModalService} from '../targeting/targeting-editor-modal.service';
import {CreativeRetailProductModalService} from './creative-retail-product-modal.service';
import {TeamApiService} from '../admin/team/team-api.service';
import {AdvertiserApiService} from '../advertiser/advertiser-api.service';

import {ExternalDemandSourceApiService} from '../admin/external-demand-source/external-demand-source-api.service';
import {LibraryCreativeApiService} from '../library-creative/library-creative-api.service';
import {MarketplaceProductApiService} from '../admin/marketplace-product/marketplace-product-api.service';
import {ProductFeedApiService} from '../common/controller/product-feed-api.service';
import {AdnRollbarService} from '../../components/util/adn-rollbar.service';
import {AssetService} from './asset/asset.service';
import {CreativeHelper} from './creative-helper.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';

import {compareById} from '../common/util/object-id-comparison';
import {CreativeApiService, CreativeModel} from './creative-api.service';
import {LineItemApiService} from '../line-item/line-item-api.service';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {TemplateViewState} from '../common/util/template-view-state';
import {
  CreativeDimensionOption,
  CreativeEditViewState,
  createCreativeEditViewState
} from './creative-edit-view-state';

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED],
  selector: 'adn-creative-edit-page',
  template
})
export class CreativeEditPageComponent extends SignalAdminResourceEditPageBase<CreativeModel> {
  [key: string]: any;

  private _creativeTypeDirect: any;
  private _lineItemInfoDirect: any;
  private setupInitialized = false;
  private creativeInitialized = false;
  private creativeBehavior: CreativeEditBehavior | null = null;
  private readonly templateViewState: TemplateViewState<CreativeEditViewState>;

  activeTab: 'design' | 'stats' | 'co2' = 'design';
  previewTab: 'preview' | 'trackers' | 'creativeTag' | 'creativeTagEmail' = 'preview';
  showingCo2 = false;
  showImpsInfo = false;
  showThirdPartyDetails = false;
  showVariables = false;
  readonly creativeView: Signal<CreativeEditViewState>;
  readonly codeMirrorOpts = {lineNumbers: true, mode: 'htmlmixed', smartIndent: false};

  get layouts(): any[] {
    return this.templateViewState.read('layouts');
  }

  set layouts(value: any[]) {
    this.templateViewState.write('layouts', value || []);
  }

  get showGeneralVariables(): boolean {
    return this.templateViewState.read('showGeneralVariables');
  }

  set showGeneralVariables(value: boolean) {
    this.templateViewState.write('showGeneralVariables', !!value);
  }

  get hasAxProductDimensions(): boolean {
    return this.templateViewState.read('hasAxProductDimensions');
  }

  set hasAxProductDimensions(value: boolean) {
    this.templateViewState.write('hasAxProductDimensions', !!value);
  }

  get getAllowedDimensions(): CreativeDimensionOption[] {
    return this.templateViewState.read('allowedDimensions');
  }

  set getAllowedDimensions(value: CreativeDimensionOption[]) {
    this.templateViewState.write('allowedDimensions', value || []);
  }

  get selectedDimension(): CreativeDimensionOption | undefined {
    return this.templateViewState.read('selectedDimension');
  }

  set selectedDimension(value: CreativeDimensionOption | undefined) {
    this.templateViewState.write('selectedDimension', value);
  }

  get optionalTargetTypes(): string[] {
    return this.templateViewState.read('optionalTargetTypes');
  }

  set optionalTargetTypes(value: string[]) {
    this.templateViewState.write('optionalTargetTypes', value || []);
  }

  get isLockedDown(): boolean {
    return this.templateViewState.read('isLockedDown');
  }

  set isLockedDown(value: boolean) {
    this.templateViewState.write('isLockedDown', !!value);
  }

  get isCreativeContentLocked(): boolean {
    return this.templateViewState.read('isCreativeContentLocked');
  }

  set isCreativeContentLocked(value: boolean) {
    this.templateViewState.write('isCreativeContentLocked', !!value);
  }

  get allComponents(): any[] {
    return this.templateViewState.read('allComponents');
  }

  set allComponents(value: any[]) {
    this.templateViewState.write('allComponents', value || []);
  }

  get acsWithAssetMatches(): Record<string, any> {
    return this.templateViewState.read('assetMatches');
  }

  set acsWithAssetMatches(value: Record<string, any>) {
    this.templateViewState.write('assetMatches', value || {});
  }

  get primaryHtmlAsset(): any {
    return this.templateViewState.read('primaryHtmlAsset');
  }

  set primaryHtmlAsset(value: any) {
    this.templateViewState.write('primaryHtmlAsset', value);
  }

  get isFullPage(): boolean {
    return this.templateViewState.read('isFullPage');
  }

  set isFullPage(value: boolean) {
    this.templateViewState.write('isFullPage', !!value);
  }

  get allowPreviews(): boolean {
    return this.templateViewState.read('allowPreviews');
  }

  set allowPreviews(value: boolean) {
    this.templateViewState.write('allowPreviews', !!value);
  }

  get allResolved(): boolean {
    return this.templateViewState.read('allResolved');
  }

  set allResolved(value: boolean) {
    this.templateViewState.write('allResolved', !!value);
  }

  get selRetailProduct(): any {
    return this.templateViewState.read('selectedRetailProduct');
  }

  set selRetailProduct(value: any) {
    this.templateViewState.write('selectedRetailProduct', value);
  }

  get isPureRetailCreative(): boolean {
    return this.templateViewState.read('isPureRetailCreative');
  }

  set isPureRetailCreative(value: boolean) {
    this.templateViewState.write('isPureRetailCreative', !!value);
  }

  get hasLibraryCreative(): boolean {
    return this.templateViewState.read('hasLibraryCreative');
  }

  set hasLibraryCreative(value: boolean) {
    this.templateViewState.write('hasLibraryCreative', !!value);
  }

  get advertiserMismatch(): any {
    return this.templateViewState.read('advertiserMismatch');
  }

  set advertiserMismatch(value: any) {
    this.templateViewState.write('advertiserMismatch', value);
  }

  get postOrphan(): boolean {
    return this.templateViewState.read('postOrphan');
  }

  set postOrphan(value: boolean) {
    this.templateViewState.write('postOrphan', !!value);
  }

  get blockSubmit(): boolean {
    return this.templateViewState.read('blockSubmit');
  }

  set blockSubmit(value: boolean) {
    this.templateViewState.write('blockSubmit', !!value);
  }

  get extDemandSources(): any[] | null {
    return this.templateViewState.read('externalDemandSources');
  }

  set extDemandSources(value: any[] | null) {
    this.templateViewState.write('externalDemandSources', value);
  }

  get chartCurrency(): any {
    return this.templateViewState.read('chartCurrency');
  }

  set chartCurrency(value: any) {
    this.templateViewState.write('chartCurrency', value);
  }

  get selPreview(): any {
    return this.templateViewState.read('previewData');
  }

  set selPreview(value: any) {
    this.templateViewState.write('previewData', value);
  }

  get selPreviewFail(): string | null {
    return this.templateViewState.read('previewFailure');
  }

  set selPreviewFail(value: string | null) {
    this.templateViewState.write('previewFailure', value);
  }

  get isDelinking(): boolean {
    return this.templateViewState.read('isDelinking');
  }

  set isDelinking(value: boolean) {
    this.templateViewState.write('isDelinking', !!value);
  }

  patchCreativeViewState(patch: Partial<CreativeEditViewState>): void {
    this.templateViewState.patch(patch);
  }

  @Input()
  set creativeType(value: any) {
    this._creativeTypeDirect = value;
  }

  get creativeType(): any {
    return this._creativeTypeDirect;
  }

  @Input()
  set lineItemInfo(value: any) {
    this._lineItemInfoDirect = value;
  }

  get lineItemInfo(): any {
    return this._lineItemInfoDirect;
  }

  constructor(
    @Inject(LocalNetworkProfile) private readonly creativeNetworkProfile: any,
    private readonly $state: StateService,
    private readonly translate: TranslateService,
    private readonly teamApi: TeamApiService,
    private readonly productFeedApi: ProductFeedApiService,
    @Inject(CreativeHelper) private readonly CreativeHelper: any,
    @Inject(AdnRollbarService) private readonly Rollbar: any,
    @Inject(AssetService) private readonly Asset: any,
    @Inject(CreativeApiService) private readonly Creative: CreativeApiService,
    private readonly creativeApi: CreativeApiService,
    @Inject(LibraryCreativeApiService) private readonly LibraryCreative: LibraryCreativeApiService,
    private readonly lineItemApi: LineItemApiService,
    @Inject(MarketplaceProductApiService) private readonly MarketplaceProduct: MarketplaceProductApiService,
    private readonly externalDemandSourceApi: ExternalDemandSourceApiService,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly advertiserApi: AdvertiserApiService,
    private readonly targetingEditorModalService: TargetingEditorModalService,
    private readonly creativeRetailProductModalService: CreativeRetailProductModalService,
    private readonly uiRouter: UIRouter,
    private readonly navigationHistory: NavigationHistoryService,
    private readonly creativeBehaviorService: CreativeEditBehaviorService,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    super(creativeApi, 'creative');
    this.templateViewState = new TemplateViewState(createCreativeEditViewState());
    this.creativeView = this.templateViewState.rendered;
  }

  override ngOnDestroy(): void {
    this.creativeBehavior?.destroy();
    this.templateViewState.destroy();
    super.ngOnDestroy();
  }

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  override isSaveDisabled(editForm: NgForm, extraDisabled = false): boolean {
    const hasDefaultName = !!(this.editingModel?.name || '').trim();
    if (!this.isNew || !this.lineItemParent || !hasDefaultName) {
      return super.isSaveDisabled(editForm, extraDisabled);
    }
    return extraDisabled
      || this.pending
      || editForm.pending
      || (editForm.invalid && !this.hasValidationError);
  }

  selectTab(tab: 'design' | 'stats' | 'co2'): void {
    this.activeTab = tab;
    if (tab === 'co2') {
      this.showingCo2 = true;
    }
    if (tab === 'stats') {
      this.showStats = true;
    }
  }

  selectPreviewTab(tab: 'preview' | 'trackers' | 'creativeTag' | 'creativeTagEmail'): void {
    this.previewTab = tab;
    if (tab === 'trackers') {
      this.hasTracker = true;
    }
    if (tab === 'creativeTag') {
      this.hasCreativeTag = true;
    }
    if (tab === 'creativeTagEmail') {
      this.hasCreativeTagEmail = true;
    }
  }

  readonly compareById = compareById;

  trackById(_index: number, item: any): string {
    return item?.id;
  }

  getTextareaRows(comp: any): number {
    const rawRows = comp.maxLength / 75 + 1;
    if (rawRows < 2) {
      return 1;
    }
    if (rawRows > 10) {
      return 10;
    }
    return Math.floor(rawRows);
  }

  protected override initialiseModel(model: CreativeModel): CreativeModel {
    const initialized = super.initialiseModel(model);
    return {
      ...initialized,
      layoutParameters: initialized.layoutParameters || {},
      cToAssets: initialized.cToAssets || {},
      cToText: initialized.cToText || {},
      cToUrls: initialized.cToUrls || {},
      cToChoices: initialized.cToChoices || {}
    };
  }

  protected override afterEditingModelReset(): void {
    this.ensureSetup();
    if (!this.creativeInitialized) {
      this.creativeInitialized = true;
      this.creativeBehavior?.afterInit();
    } else {
      this.creativeBehavior?.afterInitAfterSave();
    }
  }

  onAdvertiserMismatchFixed(saved: any): void {
    this.editPhaseApi.viewWrite(() => {
      if (saved) {
        this.editingModel.advertiser = saved.advertiser;
        this.editingModel.validationWarnings = saved.validationWarnings;
      }
      this.state.syncModel({...this.state.model(), ...saved});
      this.creativeBehavior?.afterInitAfterSave();
      this.publishModelUpdate();
    });
  }

  protected override afterSave(resource: CreativeModel): void {
    this.creativeBehavior?.afterSave(resource);
  }

  protected override prepareForSubmit(_model: CreativeModel): void {
    this.prepareCreativeForSubmit();
  }

  prepareCreativeForSubmit(): void {
    this.creativeBehavior?.beforeSubmit();
  }

  publishModelUpdate(): void {
    this.state.syncModel({...this.editingModel});
  }

  applyBehaviorSave(resource: CreativeModel): void {
    this.saveDirect(resource);
  }

  private ensureSetup(): void {
    if (this.setupInitialized || !this.model) {
      return;
    }
    this.setupInitialized = true;

    this.creativeBehavior = this.creativeBehaviorService.initialize(this, {
      model: this.model,
      readOnly: this.readOnly,
      creativeType: this.creativeType,
      lineItemInfo: this.lineItemInfo,
      $state: this.$state,
      navigationHistory: this.navigationHistory,
      routeParams: this.uiRouter.globals.params,
      translate: this.translate,
      teamApi: this.teamApi,
      productFeedApi: this.productFeedApi,
      CreativeHelper: this.CreativeHelper,
      Rollbar: this.Rollbar,
      Asset: this.Asset,
      Creative: this.Creative,
      creativeApi: this.creativeApi,
      LibraryCreative: this.LibraryCreative,
      lineItemApi: this.lineItemApi,
      MarketplaceProduct: this.MarketplaceProduct,
      externalDemandSourceApi: this.externalDemandSourceApi,
      LocalNetworkProfile: this.creativeNetworkProfile,
      LocalUserPermissions: this.LocalUserPermissions,
      Advertiser: this.advertiserApi,
      TargetingEditorModalService: this.targetingEditorModalService,
      creativeRetailProductModalService: this.creativeRetailProductModalService,
      localNetworkInfo: this.localNetworkInfo
    });
  }
}
